import {
  Component,
  HostBinding,
  Input,
  OnChanges,
  Output,
  EventEmitter,
  effect,
  OnDestroy,
} from "@angular/core";
import {
  NavItem,
  getNavItemRoutePath,
  getNavItemTrackId,
} from "./nav-item";
import { Router } from "@angular/router";
import { NavService } from "../../../../../services/nav.service";
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { TranslateModule } from "@ngx-translate/core";
import { TablerIconsModule } from "angular-tabler-icons";
import { MaterialModule } from "src/app/material.module";
import { CommonModule } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import { ContactUsComponent } from "src/app/shared/components/contact-us/contact-us.component";
import { StoreService } from "src/app/services/apps/store/store.service";
import { LoaderService } from "src/app/shared/components/loader/loader.service";
import { finalize } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { KNOWLEDGE_BASE_RETURN_STATE_KEY } from "src/app/pages/help-center/knowledge-base/knowledge-base.model";
import { resolveReturnLabelKeyFromUrl } from "src/app/pages/help-center/knowledge-base/knowledge-base-return.helper";

const MOBILE_MAX_WIDTH = 768;

@Component({
  selector: "app-nav-item",
  imports: [TranslateModule, TablerIconsModule, MaterialModule, CommonModule],
  templateUrl: "./nav-item.component.html",
  styleUrls: ["./nav-item.component.scss"],
  animations: [
    trigger("indicatorRotate", [
      state("collapsed", style({ transform: "rotate(0deg)" })),
      state("expanded", style({ transform: "rotate(180deg)" })),
      transition(
        "expanded <=> collapsed",
        animate("225ms cubic-bezier(0.4,0.0,0.2,1)"),
      ),
    ]),
  ],
})
export class AppNavItemComponent implements OnChanges, OnDestroy {
  @Output() toggleMobileLink: any = new EventEmitter<void>();
  @Output() notify: EventEmitter<boolean> = new EventEmitter<boolean>();

  expanded: any = false;
  disabled: any = false;
  twoLines: any = false;

  @HostBinding("attr.aria-expanded") ariaExpanded = this.expanded;
  @Input() item: NavItem | any;
  @Input() depth: any;

  readonly sideNavLabelTruncateLength = environment.sideNavLabelTruncateLength;

  readonly getNavItemTrackId = getNavItemTrackId;

  constructor(
    public navService: NavService,
    public router: Router,
    public dialog: MatDialog,
    private storeService: StoreService,
    private loaderService: LoaderService,
  ) {
    if (this.depth === undefined) {
      this.depth = 0;
    }

    // Close dropdown when sidebar is collapsed
    effect(() => {
      if (this.navService.sidebarCollapsed()) {
        this.setExpanded(false);
      }
    });

    effect(() => {
      this.navService.collapseAllNavDropdownsTrigger();
      if (this.hasDropdownChildren && this.expanded) {
        this.setExpanded(false);
      }
    });
  }

  private get hasDropdownChildren(): boolean {
    return !!this.item?.children?.length;
  }

  private setExpanded(expanded: boolean): void {
    this.expanded = expanded;
    this.ariaExpanded = expanded;

    if (!this.hasDropdownChildren) {
      return;
    }

    this.navService.setNavDropdownExpanded(
      getNavItemTrackId(this.item),
      expanded,
    );
  }

  ngOnChanges() {
    const url = this.navService.currentUrl();
    if (this.item.route && url) {
      const routePath = getNavItemRoutePath(this.item.route);
      this.setExpanded(url.indexOf(routePath) === 0);
    }
  }

  onItemSelected(item: NavItem) {
    if (item.block && (!item.children || !item.children.length)) {
      return;
    }

    if (!item.children || !item.children.length) {
      if (item.displayName === "Contact Us") {
        this.loaderService.show();
        this.storeService
          .get()
          .pipe(finalize(() => this.loaderService.hide()))
          .subscribe((stores: any) => {
            this.dialog.open(ContactUsComponent, {
              autoFocus: false,
              disableClose: true,
              data: { stores },
            });
          });
      } else if (item.route) {
        void this.navigateToItemRoute(item);
      }
    }
    if (item.children && item.children.length) {
      this.setExpanded(!this.expanded);
    }
    //scroll
    window.scroll({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
    if (!this.expanded) {
      if (window.innerWidth <= MOBILE_MAX_WIDTH) {
        this.notify.emit();
      }
    }
  }

  onSubItemSelected(item: NavItem) {
    if (!item.children || !item.children.length) {
      if (this.expanded && window.innerWidth <= MOBILE_MAX_WIDTH) {
        this.notify.emit();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.hasDropdownChildren) {
      this.navService.setNavDropdownExpanded(getNavItemTrackId(this.item), false);
    }
  }

  private navigateToItemRoute(item: NavItem): void {
    const routePath = getNavItemRoutePath(item.route!);
    const queryParams =
      item.queryParams ?? this.parseQueryParamsFromRoute(item.route!);

    const navigationExtras =
      routePath === "/help/knowledge-base" && queryParams?.["returnUrl"]
        ? {
            state: {
              [KNOWLEDGE_BASE_RETURN_STATE_KEY]: {
                url: queryParams["returnUrl"],
                labelKey:
                  queryParams["returnLabelKey"] ??
                  resolveReturnLabelKeyFromUrl(queryParams["returnUrl"]),
              },
            },
          }
        : undefined;

    void this.router.navigate([routePath], {
      queryParams,
      ...navigationExtras,
    });
  }

  private parseQueryParamsFromRoute(
    route: string,
  ): Record<string, string> | undefined {
    const queryIndex = route.indexOf("?");
    if (queryIndex === -1) {
      return undefined;
    }

    const params = Object.fromEntries(
      new URLSearchParams(route.slice(queryIndex + 1)).entries(),
    );

    return Object.keys(params).length > 0 ? params : undefined;
  }
}
